import React, { useState } from 'react'
import { Button } from '@/components/custom/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Plus, PencilLine, Trash2 } from 'lucide-react'
import DataTable from '@/components/custom/data-table'
import PopupConfirm from '@/components/custom/popup-confirm'
import DialogCustomerGroup from './dialog'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useShallow } from 'zustand/react/shallow'
import useDebounce from '@/hooks/use-debounce'
import { useCustomerGroupsListStore } from '@/store/settings-store/customer/groups-list-store'
import { getGroups, deleteGroup } from '@/api/settings'
import { toast } from 'sonner'
import { throwException } from '@/exceptions/throw-exception'
import QUERY_KEY from '@/constants/use-query-key.js'

export function Groups() {
  const queryClient = useQueryClient()
  const [searchText, setSearchText] = useState('')
  const debounceSearchText = useDebounce(searchText, 500)
  const [isOpenDialog, setIsOpenDialog] = useState(false)
  const [editCustomerGroup, setEditCustomerGroup] = useState(null)
  const [page, pageSize, setPage, setPageSize] = useCustomerGroupsListStore(
    useShallow((state) => [
      state.page,
      state.pageSize,
      state.setPage,
      state.setPageSize,
    ]),
  )

  const { isLoading, data: queryData } = useQuery({
    queryKey: [
      QUERY_KEY.GET_LIST_CUSTOMER_GROUPS,
      { page, pageSize, search: debounceSearchText },
    ],
    queryFn: async () => {
      const params = {
        target: 'end_user',
        offset: (page - 1) * pageSize,
        limit: pageSize,
      }

      if (searchText) {
        params.name = searchText
      }

      try {
        const res = await getGroups({ params })
        return res.data || {}
      } catch (error) {
        throwException(error)
        return []
      }
    },
  })

  const data = isLoading ? [] : queryData?.list || []
  const total = isLoading ? 0 : queryData?.total || 0

  const { mutate: handleDeleteCustomerGroup } = useMutation({
    mutationFn: (customerGroupId) => deleteGroup({ id: customerGroupId }),
    onSuccess: () => {
      toast.success('Xóa nhóm khách hàng thành công')

      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.GET_LIST_CUSTOMER_GROUPS],
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.GET_ALL_CUSTOMER_GROUPS],
      })
    },
    onError: throwException,
  })

  const handleSetEditCustomerGroup = (customerGroup) => {
    setIsOpenDialog(true)
    setEditCustomerGroup(customerGroup)
  }

  const columns = [
    {
      accessorKey: 'name',
      header: () => <div>Tên nhóm khách hàng</div>,
    },
    {
      accessorKey: 'action',
      header: () => <div className="text-center">Hành động</div>,
      className: 'justify-center',
      cell: ({ row }) => {
        const customerGroup = row.original

        return (
          <div className="flex gap-4">
            <PencilLine
              className="h-4 w-4"
              onClick={(e) => {
                e.stopPropagation()
                handleSetEditCustomerGroup(customerGroup)
              }}
            />

            <PopupConfirm
              trigger={
                <Trash2 className="h-4 w-4 cursor-pointer text-[hsl(var(--icon-danger))]" />
              }
              title={'Xóa nhóm khách hàng'}
              description={`Bạn có chắc chắn muốn xóa nhóm khách hàng ${customerGroup.name} không?`}
              onConfirm={() => handleDeleteCustomerGroup(customerGroup.id)}
            />
          </div>
        )
      },
    },
  ]

  return (
    <React.Fragment>
      <div className="flex h-full w-full flex-col overflow-hidden">
        <Card className="h-full overflow-hidden">
          <CardContent className="flex h-full flex-col gap-2 p-4">
            <div className="sticky top-0 flex flex-col gap-2">
              <h1 className="text-lg font-bold">Nhóm khách hàng</h1>
              <div className="grid w-full grid-cols-3">
                <div className="col-span-1">
                  <Input
                    type="text"
                    placeholder="Tìm kiếm theo tên nhóm khách hàng"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </div>
                <div className="col-span-2 flex justify-end">
                  <Button
                    leftSection={<Plus className="h-4 w-4" />}
                    onClick={() => setIsOpenDialog(true)}
                  >
                    Thêm nhóm khách hàng
                  </Button>
                </div>
              </div>
            </div>

            <DataTable
              columns={columns}
              data={data}
              isLoading={isLoading}
              total={total}
              page={page}
              setPage={setPage}
              pageSize={pageSize}
              setPageSize={setPageSize}
            />
          </CardContent>
        </Card>
      </div>

      {isOpenDialog && (
        <DialogCustomerGroup
          open={isOpenDialog}
          customerGroup={editCustomerGroup}
          handleClose={() => {
            setIsOpenDialog(false)
            setEditCustomerGroup(null)
          }}
        />
      )}
    </React.Fragment>
  )
}
